import { useFormikContext, type FormikHelpers } from 'formik'
import * as React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import Portfolio from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { type FileState } from 'src/hooks/fileUpload'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import PopoverComponent from 'src/views/components/PopoverComponent'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import FileInput from 'src/views/components/forms/formik/FileInput'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface CompetencyCriteria {
  criteriaId: number
  text: string
}

interface Competency {
  competencyId: number
  criterias: CompetencyCriteria[]
}

interface FormikValues {
  readonly mentorId: string
  readonly text: string
  readonly id: string
  readonly files: readonly FileState[]
  readonly competencies: Competency[]
}

export default function StudentPortfolioWorkAddPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:portfolio_create_an_assignment')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const params = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sectionId = params.id ?? ''
  const hasValidId = sectionId.length > 0
  const redirectPath = `/${locale}/student/portfolio/sections/${sectionId}/works`

  const section = useApi({
    endpoint: Api.getStudentPortfolioSection,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
    shouldFetch: hasValidId,
    suspense: false,
  })

  const comps = useApi({
    endpoint: Api.getStudentPortfolioCompetencies,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
    shouldFetch: hasValidId,
    suspense: false,
  })

  const sectionData = section.data
  const compsData = comps.data?.data
  const workType = sectionData?.workType
  const isType2 = workType === '2'
  const isType3 = workType === '3'

  const initialValues = React.useMemo<FormikValues>(() => {
    const baseValues = {
      id: sectionId,
      mentorId: '',
      text: '',
      files: [] as readonly FileState[],
    }

    if (isType2) {
      return {
        ...baseValues,
        competencies:
          compsData?.map((c) => ({
            competencyId: Number(c.id),
            criterias:
              c.criterias?.map((criteria) => ({
                criteriaId: Number(criteria.id),
                text: '',
              })) ?? [],
          })) ?? [],
      }
    } else if (isType3) {
      return {
        ...baseValues,
        competencies:
          compsData?.map((c) => ({
            competencyId: Number(c.id),
            criterias: [],
          })) ?? [],
      }
    } else {
      return { ...baseValues, competencies: [] }
    }
  }, [compsData, sectionId, isType2, isType3])

  const onSubmit = React.useCallback(
    async (
      { mentorId, files, text, competencies }: FormikValues,
      formikHelpers: FormikHelpers<FormikValues>
    ): Promise<void> => {
      if (!hasValidId) return

      const filteredCompetencies = isType2 ? competencies : competencies.map(({ competencyId }) => ({ competencyId }))

      await Api.putStudentPortfolioDraftWork({
        headers,
        body: {
          mentorId,
          ...(text && { text }),
          competencies: filteredCompetencies,
          files: files.flatMap((e) => e.remoteFile?.id ?? []),
        },
        args: { id: sectionId },
      })

      toast.success(t('portfolio:assignment_submitted'))
      formikHelpers.resetForm()
      setIsSubmitted(true)
    },
    [isType2, headers, sectionId, t, hasValidId]
  )

  React.useEffect(() => {
    if (isSubmitted) {
      navigate(redirectPath)
    }
  }, [isSubmitted, navigate, redirectPath])

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:portfolio'), path: `/${locale}/student/portfolio` },
      { page: sectionData?.name ?? '', path: pathname },
      { page: t('portfolio:portfolio_create_an_assignment'), path: pathname },
    ],
    [t, locale, sectionData?.name, pathname]
  )

  // Show loading while data is being fetched
  if (sectionData == null || compsData == null) {
    return null
  }

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit} isConfirmable={!isSubmitted}>
      <Section
        title={t('portfolio:portfolio_create_an_assignment')}
        icon={<Portfolio />}
        breadcrubms={breadcrumbsItems}
        rightElement={<BackButton link={redirectPath} />}
      >
        <SuspenseWrapper>
          <FormContent section={sectionData} sectionId={sectionId} />
        </SuspenseWrapper>
      </Section>
    </Form>
  )
}
interface FormContentProps {
  readonly section: Api.Section
  readonly sectionId: string
}

function FormContent({ section, sectionId }: FormContentProps): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const [fileInputId] = React.useState(() => `${sectionId}-${Date.now()}`)

  const mentors = useApi({
    endpoint: Api.getStudentPortfolioMentros,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
  })

  const sectionCompetencies = useApi({
    endpoint: Api.getStudentPortfolioCompetencies,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
  })

  const workType = section.workType
  const isType2 = workType === '2'
  const isType3 = workType === '3'
  const competenciesData = sectionCompetencies.data.data

  return (
    <div className="w-full rounded-card bg-card px-6 py-9">
      <FormError />
      <div className="mb-4 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
        {t('portfolio:competence')}
        <span className="pl-1 text-lightSecondaryWarning">*</span>
      </div>

      {competenciesData != null && isType2 && (
        <>
          {competenciesData.map((competencyItem, index) => (
            <div key={competencyItem.id} className="mb-6 rounded-sm border border-borderGrey p-4">
              <div className="mb-3 mr-4 flex items-center">
                <label className="text-primaryTextColor" htmlFor={competencyItem.id}>
                  <span className="font-bold">
                    {competencyItem.name}
                    <PopoverComponent>
                      <span
                        className="mb-3 flex py-2 leading-6"
                        dangerouslySetInnerHTML={{ __html: competencyItem.description }}
                      />
                      {competencyItem.criterias?.map((criteria) => (
                        <div key={criteria.id} className="my-2">
                          <label className="mb-3 text-primaryTextColor">
                            <div className="my-3">
                              {criteria.name}
                              <div>
                                <span className="p-2" dangerouslySetInnerHTML={{ __html: criteria.description }} />
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </PopoverComponent>
                  </span>
                </label>
              </div>
              <div className="flex">
                {competencyItem.criterias?.map((criteria, criteriaIndex) => (
                  <div key={criteria.id} className="mb-3 mr-2 flex w-full">
                    <TextareaInput
                      label={criteria.name}
                      name={`competencies[${index}]criterias[${criteriaIndex}]text`}
                      rows={5}
                      maxLength={section.wordLimits?.max}
                      minLength={section.wordLimits?.min}
                      required
                      countMode="words"
                      instructions={
                        criteria.description ? (
                          <PopoverComponent>
                            <span dangerouslySetInnerHTML={{ __html: criteria.description }} />
                          </PopoverComponent>
                        ) : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {competenciesData != null && !isType2 && (
        <div className="mb-6">
          {competenciesData.map((c) => (
            <div key={c.id}>
              <div className="mb-3 mr-4 flex items-center">
                <input
                  name="competencies"
                  type="checkbox"
                  id={c.id}
                  className="border border-tableBorderColor"
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      formik.setFieldValue('competencies', [...formik.values.competencies, { competencyId: c.id }])
                    } else if (!isType3) {
                      formik.setFieldValue(
                        'competencies',
                        formik.values.competencies.filter((v) => v.competencyId.toString() !== c.id)
                      )
                    }
                  }}
                  checked={formik.values.competencies.some((v) => v.competencyId.toString() === c.id) || isType3}
                  disabled={isType3}
                />
                <label className="text-primaryTextColor" htmlFor={c.id}>
                  <span className="ml-2">
                    {c.name}
                    <PopoverComponent>
                      <span className="mb-3 flex py-2 leading-6" dangerouslySetInnerHTML={{ __html: c.description }} />
                      {c.criterias?.map((criteria) => (
                        <div key={criteria.id} className="my-2">
                          <label className="mb-3 text-primaryTextColor">
                            <div className="my-3">
                              {criteria.name}
                              <div>
                                <span className="p-2" dangerouslySetInnerHTML={{ __html: criteria.description }} />
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </PopoverComponent>
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6">
        <SelectInput
          placeholder={t('common:choose')}
          label={t('portfolio:mentor')}
          name="mentorId"
          options={
            mentors.data
              .filter((mentor) => mentor.manage?.canChoose)
              .map((mentor) => ({
                label: `${mentor.firstName} ${mentor.lastName}`,
                value: mentor.id,
              })) ?? []
          }
        />
      </div>
      {!isType2 && (
        <div>
          <div className="mb-2 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
            {t('portfolio:add_assignment_description')}
          </div>
          <FormikTinyMce
            name="text"
            countMode="words"
            minLength={section.wordLimits?.min}
            maxLength={section.wordLimits?.max}
          />
        </div>
      )}

      <div className="my-6" style={{ zIndex: 0, maxWidth: 1065 }}>
        <FileInput
          key={fileInputId}
          multiple
          config={Api.UploadConfigMap.studentPortfolio(sectionId)}
          onChange={(files) => void formik.setFieldValue('files', files)}
        />
      </div>
      <div className="flex w-full justify-end">
        <Button type="submit" variant="blue" disabled={!formik.isValid || !formik.dirty}>
          {t('common:save_as_draft')}
        </Button>
      </div>
    </div>
  )
}
